<template>
  <div ref="container" class="absolute bottom-0 mb-[65px] w-full pb-safe-area">
    <transition name="slide">
      <div
        v-if="isShowSpotCard && items.length !== 0"
        ref="spotList"
        id="spotCardList"
        class="flex gap-2 overflow-x-auto snap-x snap-mandatory pl-[40%] pr-[40%]"
        @scroll="onScroll"
        @touchstart="holdTapStartPos"
        @touchmove="setCalculatedPullDistance"
        @touchend="pullAction"
      >
        <!-- バウンスのために最後のカードに対して、200pxのmarginを用意 -->
        <div
          v-for="(comment, idx) in items"
          :key="comment.id"
          v-bind="bindItem(idx)"
          :aria-selected="idx === activeIndex"
          class="last:snap-align-none"
        >
          <!-- スポットカード -->
          <div
            class="snap-center w-[299px] h-[194px] flex-1 pl-4 py-4 pr-3 bg-white w-full rounded-[10px]"
          >
            <SpotCard
              :currentPosition="currentPosition"
              :comment="comment"
              @click="clickCard(idx)"
            />
          </div>
        </div>
      </div>
    </transition>
    <!-- ページネーションアイコン -->
    <transition name="pagination">
      <div
        v-show="issShowPagingIcon"
        class="absolute top-[45%] right-0 bg-white h-[7.5vw] w-[7.5vw] rounded-[39px] flex justify-center items-center mr-[2vw]"
      >
        <BaseLoading width="16" />
      </div>
    </transition>
  </div>
</template>

<script>
import SpotCard from '@/components/molecules/dokoiku/SpotCard.vue'
import BaseLoading from '@/components/atoms/BaseLoading.vue'
import log from 'loglevel'

/** スクロール位置の状態 */
const SCROLL_STATUS = {NONE: 'NONE', RIGHT: 'RIGHT', LEFT: 'LEFT'}

/** ページネーション時の判定値 */
const PAGINATION_WIDTH = 100

/** 最大取得スポット数 */
const MAX_SPOT = 100

const HorizontalSpotCardSlide = {
  name: 'HorizontalSpotCardSlide',
  components: {
    SpotCard,
    BaseLoading,
  },
  emits: ['change', 'pagination', 'transition', 'scrolling', 'scroll-end'],
  props: {
    items: {type: Array, required: true}, // カード一覧
    isShowSpotCard: {type: Boolean, default: false}, // カード表示非表示フラグ(マップ操作と連動)
    centerOffset: {type: Number, default: 0}, // 選択カードを決めるための基準値をずらす
    stopDebounceMs: {type: Number, default: 0}, // 停止判定の待ち時間(ms)
    tappedPinIndex: {type: Number, default: 0}, // タップした標準ピンのインデックス
  },
  data() {
    return {
      activeIndex: 0, // 選択中カードのインデックス
      itemEls: [], // カード要素
      scrollTimer: null, // スクロール中のタイマー
      isScrolling: false, // スクロール中である
      lastEmittedIndex: null, // 最後に連携したカードのインデックス
      startX: null, // タッチ開始位置
      issShowPagingIcon: false, // ページネーションアイコン表示有無フラグ
      horizontalScrollAmount: 0, // 端までスクロールした後の横スクロール量
      scrollStatus: SCROLL_STATUS.RIGHT, // スクロール位置の状態
      isContinue: true, // ページネーション有無
    }
  },
  watch: {
    tappedPinIndex: {
      async handler(value) {
        this.$nextTick(() => {
          this.scrollToIndex(value)
        })
      },
    },
  },
  computed: {
    /**
     * 現在地情報
     */
    currentPosition() {
      return this.$store.state.currentPosition
    },
    /** スクロール位置が端まで移動していない */
    isNoneOfScrollStatus() {
      return this.scrollStatus === SCROLL_STATUS.NONE
    },
    /** ページネーションアクションである*/
    isPaginationAction() {
      return (
        this.scrollStatus === SCROLL_STATUS.RIGHT &&
        PAGINATION_WIDTH < this.horizontalScrollAmount
      )
    },
    /** 追加スポットの取得可能である */
    isPossibleFetchSpot() {
      return this.isContinue && this.items.length < MAX_SPOT
    },
  },
  mounted() {
    this.recalculateActive() // 初期確定

    // ブラウザがscrollendをサポートしていれば利用（なくてもOK）
    if ('onscrollend' in window || 'onscrollend' in document) {
      this.$refs.container.addEventListener('scrollend', this.onScrollEnd, {
        passive: true,
      })
    }
  },
  activated() {
    /** 既に選択済があれば、対象カードに移動 */
    this.activeIndex = this.tappedPinIndex
    this.scrollToIndex(this.tappedPinIndex, false)
  },
  beforeUnmount() {
    if (this.$refs.container) {
      this.$refs.container.removeEventListener('scrollend', this.onScrollEnd)
    }
  },
  methods: {
    /**
     * 選択カードの設定
     * @param {number} index カードのインデックス
     * @returns {object} 選択情報か通常カードの要素情報
     */
    bindItem(index) {
      return {
        ref: (el) => this.registerItemEl(index, el),
        class: {'is-active': index === this.activeIndex},
      }
    },
    /**
     * カード要素をメモリに登録
     * @param index カードインデックス
     * @param el カード要素
     */
    registerItemEl(index, el) {
      this.itemEls[index] = el
    },
    /**
     * 追加スポット有無情報の設定
     * @param isContinue 続きのスポットがあるか
     */
    setIsContinue(isContinue) {
      this.isContinue = isContinue
    },
    /** スクロール中のアクション */
    onScroll() {
      // スクロール中
      this.isScrolling = true

      // スクロール位置の更新
      this.updateScrollStatus()

      this.$emit('scrolling', this.isScrolling)

      /** スクロールするたびに、リセットと再設定をする */
      clearTimeout(this.scrollTimer)
      this.scrollTimer = setTimeout(this.onScrollEnd, this.stopDebounceMs)
    },

    /** スクロール停止後のアクション */
    onScrollEnd() {
      // 最終位置で再計算して選択カードの切り替え
      const changed = this.recalculateActive()

      // スクロール停止
      this.isScrolling = false

      // 停止時の確定通知
      // 選択ピンへの連携
      if (changed || this.lastEmittedIndex !== this.activeIndex) {
        this.emitSpotCard('change')
      }
      this.$emit('scroll-end')
    },

    updateScrollStatus() {
      const el = document.getElementById('spotCardList')
      // 画面遷移等で要素が破棄されている場合、何も行わない
      if (el == null) return

      const {scrollLeft, scrollWidth, clientWidth} = el
      const {paddingRight} = window.getComputedStyle(el)
      const transformFloat = (value) => {
        return parseFloat(value.replace('px', ''))
      }

      /** スクロールが一番右まで行われているか */
      // 要素からpaddingRightを取得し、その分を取り除く
      const lastCardPaddingRight = transformFloat(paddingRight)
      const isScrollRight =
        scrollLeft + clientWidth >= scrollWidth - lastCardPaddingRight

      // スクロール状態を保持
      if (isScrollRight) {
        this.scrollStatus = SCROLL_STATUS.RIGHT
      } else {
        this.scrollStatus = SCROLL_STATUS.NONE
      }
    },
    /**
     * 選択カードのタップ時の挙動
     * 選択中カードであれば、画面遷移
     * 左右のカードであれば。スクロール
     * @param selectedIndex
     */
    clickCard(selectedIndex) {
      // スクロール中は処理させない
      if (this.isScrolling) return

      if (this.activeIndex === selectedIndex) {
        this.emitSpotCard('transition')
        return
      }
      this.scrollToIndex(selectedIndex)
    },

    /** 選択カードの切り替え */
    recalculateActive() {
      const container = this.$refs.container
      if (!container || this.itemEls.length === 0) return false // eslint-disable-line no-magic-numbers
      const containerRect = container.getBoundingClientRect()

      // 本要素の中心位置抽出
      const containerCenter =
        containerRect.left + containerRect.width / 2 + this.centerOffset //eslint-disable-line no-magic-numbers

      /** スクロール停止後の選択されたカードのインデックス抽出 */
      let stopCardIndex = 0
      let closestDist = Infinity
      for (let i = 0; i < this.itemEls.length; i++) {
        const el = this.itemEls[i]
        if (!el) continue
        const rect = el.getBoundingClientRect()
        const center = rect.left + rect.width / 2 // eslint-disable-line no-magic-numbers
        const dist = Math.abs(center - containerCenter)
        if (dist < closestDist) {
          closestDist = dist
          stopCardIndex = i
        }
      }

      /** 選択状態の切り替え */
      const changed = stopCardIndex !== this.activeIndex
      if (changed) {
        this.activeIndex = stopCardIndex
      }
      return changed
    },
    /**
     * タッチ開始時処理
     * タッチ開始位置を保持する
     * @param {TouchEvent} event タッチイベント
     */
    holdTapStartPos(event) {
      this.issShowPagingIcon = false
      // スクロール位置が最左部もしくは最右部以外であれば、何もしない
      if (this.isNoneOfScrollStatus) return

      // タッチ開始位置を保持
      this.startX = event.touches[0].pageX // eslint-disable-line no-magic-numbers
    },
    /**
     * タッチ移動時処理
     * スワイプの移動距離を算出し、どこまで引いたのかを距離として保持する
     * @param {TouchMove} event タッチイベント
     */
    setCalculatedPullDistance(event) {
      // スクロール位置が最上部もしくは最下部以外であれば、何もしない
      if (this.isNoneOfScrollStatus) return
      const currentX = event.touches[0].pageX // eslint-disable-line no-magic-numbers
      const diffTouchX = currentX - this.startX

      // 移動していない場合は何もしない
      if (diffTouchX === 0) return // eslint-disable-line no-magic-numbers

      // スクロール位置が最右部の場合であり、左向きにスワイプした場合、引きの値を算出し更新
      if (this.scrollStatus === SCROLL_STATUS.RIGHT) {
        this.horizontalScrollAmount = Math.abs(diffTouchX)
      }
    },
    /**
     * 引き上げ操作時処理
     * カードを一定以上引いた場合、ページネーション処理を行う
     */
    pullAction() {
      /** 一番右のスクロール位置で、一定以上引いた場合、ページネーション操作と判断する */
      /** 取得済スポット数が上限を超えていないかつ追加スポットがある */
      if (this.isPaginationAction && this.isPossibleFetchSpot) {
        this.issShowPagingIcon = true // ページネーションアイコン表示
        this.$emit('pagination')
      }
      this.horizontalScrollAmount = 0
      this.startX = null
    },
    /**
     * ページネーション完了時の終了処理
     * @param {number} offset 次カードのオフセット
     * @param {number} fetchAmount 取得件数
     */
    paginationDone(offset, fetchAmount) {
      // スクロール位置が最右部の場合、要素が追加された時に不自然な挙動となる。わずかにでも最下部でなければ回避できるため、スクロール位置をずらす
      const el = document.getElementById('spotCardList')
      if (el != null) el.scrollLeft = el.scrollLeft - 0.01 // eslint-disable-line no-magic-numbers

      this.issShowPagingIcon = false

      // エラー、0件取得時はここで終了
      if (!fetchAmount) return

      // スクロール位置を取得したリストの先頭にする
      this.$nextTick(() => {
        this.changeSpotCard(el, offset, fetchAmount)
      })
    },
    /**
     * 選択カードの対象を更新
     * @param {element} scrollElements カードリストの要素
     * @param {number} nextSpotOffset 次カードのオフセット値
     * @param {number} fetchAmount APIから取得した追加カードリストの件数
     */
    changeSpotCard(scrollElements, nextSpotOffset, fetchAmount) {
      const lastSpotIndex = this.items.length - 1 // eslint-disable-line no-magic-numbers
      // ページネーション後の選択カードの対象
      const targetCardPosition =
        0 < fetchAmount ? nextSpotOffset : lastSpotIndex // eslint-disable-line no-magic-numbers
      const containerRect = scrollElements.getBoundingClientRect()
      const targetRect =
        this.itemEls[targetCardPosition].getBoundingClientRect()
      const currentLeft = scrollElements.scrollLeft

      let targetLeft = currentLeft + (targetRect.left - containerRect.left)

      // バウンスを元に戻すアニメーション
      requestAnimationFrame(() => {
        if ('scrollTo' in scrollElements) {
          scrollElements.scrollTo({left: targetLeft, behavior: 'smooth'})
        }
      })
    },
    /**
     * 指定のスポットカードへ自動スクロールさせる
     * @param index タップされたピンのインデックス
     * @param isAnimation アニメーションの有無
     */
    async scrollToIndex(index, isAnimation = true) {
      /* eslint-disable no-magic-numbers */
      const container = this.$refs.spotList
      if (!container || index < 0 || index >= this.items.length) return
      await this.$nextTick()
      const targetEl = this.itemEls[index]
      if (!targetEl) return

      const cRect = container.getBoundingClientRect()
      const eRect = targetEl.getBoundingClientRect()
      const currentLeft = container.scrollLeft

      let targetLeft
      const cCenter = cRect.left + container.clientWidth / 2
      const eCenter = eRect.left + eRect.width / 2
      targetLeft = currentLeft + (eCenter - cCenter)

      const maxLeft = container.scrollWidth - container.clientWidth
      targetLeft = Math.max(0, Math.min(targetLeft, maxLeft))

      // スナップ使用中は一時無効化すると確実
      requestAnimationFrame(() => {
        if ('scrollTo' in container) {
          container.scrollTo({
            left: targetLeft,
            behavior: isAnimation ? 'smooth' : 'auto',
          })
        }
      })
      /* eslint-enable no-magic-numbers */
    },
    /**
     * スポット情報を通知
     * 選択ピンの更新時、カードタップ時に使うため
     * @param {string} emittingName 通知先名
     */
    emitSpotCard(emittingTarget) {
      const payload = {
        index: this.activeIndex,
        item: this.items[this.activeIndex],
      }
      this.lastEmittedIndex = this.activeIndex

      switch (emittingTarget) {
        case 'change':
          this.$emit(emittingTarget, payload)
          break
        case 'transition':
          this.$emit(emittingTarget, this.items[this.activeIndex].spot)
          break
        default:
      }
    },
    init() {
      // eslint-disable-next-line
      this.activeIndex = 0
      this.lastEmittedIndex = null
      this.itemEls = []
      this.isContinue = true
      this.startX = null
    },
    unmountProcess() {
      this.init()
    },
  },
}
export default HorizontalSpotCardSlide
</script>

<style scoped>
#spotCardList::-webkit-scrollbar {
  /* スクロールバー非表示 */
  display: none;
}
/* カード降下のトランジション */
.slide-enter-active,
.slide-leave-active {
  transform: translate(0px, 0px) translateZ(1px);
  transition: transform 500ms;
}
/* カード上昇のトランジション */
.slide-enter-from,
.slide-leave-to {
  transform: translateY(100vh) translateY(0px) translateZ(1px);
  transition: transform 700ms;
}

.carousel {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
}
.carousel ::v-deep .card {
  flex: 0 0 auto;
  scroll-snap-align: center;
}
.carousel ::v-deep .card.is-active {
  outline: 2px solid #3b82f6;
  transform: scale(1.02);
}
</style>
